import { useCapacityCalendarContext } from "./capacity-calendar";
import { type CrewDefinitionListItem, useCrewDefinitions } from "@/hooks/use-crew-definitions";
import { type ScheduledCrewList, useScheduledCrews } from "@/hooks/use-scheduled-crews";

/** Which crews hold which ticket across the visible window, and the crews themselves by id. */
export interface StaffingIndex {
	/** The crews scheduled onto each ticket, in the order their stretches were read, without repeats. */
	crewIdsByTicket: ReadonlyMap<string, readonly string[]>;
	/** Every standing crew of the organisation, by id. */
	crewById: ReadonlyMap<string, CrewDefinitionListItem>;
}

const EMPTY_CREWS: readonly string[] = [];

/**
 * The two indexes every row of the board asks the same question of: is anybody on this ticket,
 * and who.
 *
 * Both answers come off the board's two shared reads, and every row used to derive them for itself
 * — one pass over every stretch per ticket row, one over every stretch per coverage bar — which is
 * a cost of rows times stretches on every render. The indexes are built once per answer instead:
 * they are memoised on the array the query handed out, so every row on the board shares one index
 * until the next refetch replaces the array.
 */
export function useStaffingIndex(): StaffingIndex {
	/** Context */
	const { controller } = useCapacityCalendarContext();

	/** Query */
	const { ticketAssignments } = useScheduledCrews(controller.windowStart, controller.windowEnd);
	const { crewDefinitions } = useCrewDefinitions();

	/** Computed */
	return {
		crewIdsByTicket: indexStretches(ticketAssignments),
		crewById: indexCrews(crewDefinitions),
	};
}

/** The crews holding a ticket, or none. */
export function crewsHolding(index: StaffingIndex, ticketId: string): readonly string[] {
	return index.crewIdsByTicket.get(ticketId) ?? EMPTY_CREWS;
}

const stretchIndexes = new WeakMap<ScheduledCrewList, ReadonlyMap<string, readonly string[]>>();
const crewIndexes = new WeakMap<
	CrewDefinitionListItem[],
	ReadonlyMap<string, CrewDefinitionListItem>
>();

function indexStretches(assignments: ScheduledCrewList) {
	const existing = stretchIndexes.get(assignments);
	if (existing) return existing;

	const byTicket = new Map<string, string[]>();
	for (const assignment of assignments) {
		const crews = byTicket.get(assignment.ticketId);
		if (!crews) byTicket.set(assignment.ticketId, [assignment.crewDefinitionId]);
		else if (!crews.includes(assignment.crewDefinitionId)) crews.push(assignment.crewDefinitionId);
	}
	stretchIndexes.set(assignments, byTicket);
	return byTicket;
}

function indexCrews(crewDefinitions: CrewDefinitionListItem[]) {
	const existing = crewIndexes.get(crewDefinitions);
	if (existing) return existing;

	const byId = new Map<string, CrewDefinitionListItem>();
	for (const crewDefinition of crewDefinitions) byId.set(crewDefinition.id, crewDefinition);
	crewIndexes.set(crewDefinitions, byId);
	return byId;
}
